import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/AuthContext';
import OnCallStatusWidget from '../components/OnCallStatusWidget';
import ClinicianActionModal from '../components/ClinicianActionModal';
import api from '../api/client';
import { useAiCache, AI_TTL } from '../contexts/AiCacheContext';
import webSocketEventBus from '../services/webSocketEventBus';
import toast from 'react-hot-toast';
import {
  Activity, AlertTriangle, CheckCircle, UserCircle, FileText,
  Stethoscope, Heart, Phone, ClipboardList, Filter, RefreshCw,
  ArrowRight, Home as HomeIcon, UserPlus, Shield, Clock,
  TrendingUp, TrendingDown, Minus, Users, Send, X, ChevronDown,
  Video, CalendarClock, Pill, ChevronRight, Zap, Search
} from 'lucide-react';

/**
 * Sparkline — renders a mini SVG line chart from an array of { score } points.
 */
const Sparkline = ({ points, color = '#00478d', height = 28, width = 80 }) => {
  if (!points?.length || points.length < 2) return null;
  const scores = points.map(p => p.score);
  const min = Math.min(...scores);
  const max = Math.max(...scores);
  const range = max - min || 1;
  const xs = points.map((_, i) => (i / (points.length - 1)) * width);
  const ys = scores.map(s => height - ((s - min) / range) * height);
  const d = xs.map((x, i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${ys[i].toFixed(1)}`).join(' ');
  const lastY = ys[ys.length - 1];
  const trend = scores[scores.length - 1] - scores[0];
  const lineColor = trend > 5 ? '#dc2626' : trend < -5 ? '#16a34a' : color;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} fill="none" className="shrink-0">
      <path d={d} stroke={lineColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={xs[xs.length - 1]} cy={lastY} r="2.5" fill={lineColor} />
    </svg>
  );
};

/**
 * DoctorDashboard — Clinician Action Hub.
 * Pure content, no chrome (AppLayout wraps this).
 */
const AUTO_REFRESH_MS = 3 * 60 * 1000; // 3 minutes

const DoctorDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { getOrFetch } = useAiCache();

  // ── Loading states: isLoading = first load, isFetching = silent background refresh
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);

  // ── Per-widget data
  const [stats, setStats] = useState(null);
  const [priorityPatients, setPriorityPatients] = useState([]);
  const [recentCareEvents, setRecentCareEvents] = useState([]);
  const [delegatedActions, setDelegatedActions] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [requestSearch, setRequestSearch] = useState('');
  const [consultAction, setConsultAction] = useState(null); // { symptomId, action: 'accept'|'decline' }

  // ── "New" tracking: events newer than lastSeenAt are flagged as unseen
  const [lastSeenAt] = useState(() => {
    const stored = localStorage.getItem('doctor_dashboard_last_seen');
    return stored ? new Date(stored) : new Date(0);
  });
  // Mark all as seen when the dashboard mounts
  useEffect(() => {
    localStorage.setItem('doctor_dashboard_last_seen', new Date().toISOString());
  }, []);

  // ── Per-widget errors
  const [errors, setErrors] = useState({ stats: false, priority: false, events: false, delegated: false, requests: false });

  // ── Dashboard tab: 'queue' = priority actions (default), 'insights' = health trends
  const [activeTab, setActiveTab] = useState('queue');

  // ── Action queue state
  const [executingAction, setExecutingAction] = useState(null);
  const [dismissedCards, setDismissedCards] = useState([]);
  const [snoozingCard, setSnoozingCard] = useState(null); // originalIndex being snoozed
  const [stabilizingCards, setStabilizingCards] = useState({});

  // ── Schedule state
  const [appointments, setAppointments] = useState([]);

  // ── AI summaries: patientId → { summary, loading }
  const [aiSummaries, setAiSummaries] = useState({});
  // ── Risk trends: patientId → [{ score, date }]
  const [riskTrends, setRiskTrends] = useState({});

  // ── Follow-up scheduling modal state
  const [followUpModal, setFollowUpModal] = useState(null); // { patientId, assessmentId, actionType, cardIndex }

  // ── Delegation state
  const [delegateModal, setDelegateModal] = useState(null);
  const [careTeamDoctors, setCareTeamDoctors] = useState([]);
  const [loadingCareTeam, setLoadingCareTeam] = useState(false);
  const [delegating, setDelegating] = useState(false);
  const [selectedDoctorId, setSelectedDoctorId] = useState('');
  const [delegationNote, setDelegationNote] = useState('');

  // ── Relative time ticker (re-renders every 30s to keep "Updated X min ago" fresh)
  const [, setTick] = useState(0);
  const autoRefreshRef = useRef(null);
  const tickRef = useRef(null);

  const getRelativeTime = (date) => {
    if (!date) return null;
    const diff = Math.floor((Date.now() - date.getTime()) / 1000);
    if (diff < 10) return 'Just now';
    if (diff < 60) return `${diff}s ago`;
    const m = Math.floor(diff / 60);
    if (m < 60) return `${m} min ago`;
    return `${Math.floor(m / 60)}h ago`;
  };

  // Silent fetch — no loading spinner if data already exists
  const fetchData = useCallback(async ({ silent = false } = {}) => {
    if (silent) {
      setIsFetching(true);
    } else {
      setIsLoading(true);
    }

    const [statsRes, priorityRes, eventsRes, delegatedRes, scheduleRes, requestsRes, consultsRes] = await Promise.allSettled([
      api.get('/symptoms/stats/dashboard'),
      api.get('/risk-assessments/pending?minScore=60&limit=10'),
      api.get('/care-events/recent?hours=48'),
      api.get('/risk-assessments/delegated-to-me'),
      api.get('/care-events/my-appointments?days=7'),
      api.get('/care-requests/received-requests?status=pending&limit=50'),
      api.get('/responses/pending-consults'),
    ]);

    // Per-widget: update data only if that fetch succeeded
    setErrors({
      stats:     statsRes.status     === 'rejected',
      priority:  priorityRes.status  === 'rejected',
      events:    eventsRes.status    === 'rejected',
      delegated: delegatedRes.status === 'rejected',
      requests:  requestsRes.status  === 'rejected',
    });
    if (statsRes.status     === 'fulfilled' && statsRes.value?.data?.success)   setStats(statsRes.value.data.data);
    if (priorityRes.status  === 'fulfilled' && priorityRes.value?.data?.data)   setPriorityPatients(priorityRes.value.data.data);
    if (eventsRes.status    === 'fulfilled' && eventsRes.value?.data?.data)     setRecentCareEvents(eventsRes.value.data.data);
    if (delegatedRes.status === 'fulfilled' && delegatedRes.value?.data?.data)  setDelegatedActions(delegatedRes.value.data.data);
    if (scheduleRes.status  === 'fulfilled' && scheduleRes.value?.data?.data)   setAppointments(scheduleRes.value.data.data);
    // Merge care-team requests and consult requests into one list, sorted newest first
    const careReqs = requestsRes.status === 'fulfilled' ? (requestsRes.value?.data?.data || []) : [];
    const consultReqs = consultsRes.status === 'fulfilled' ? (consultsRes.value?.data?.data || []) : [];
    const merged = [...careReqs.map(r => ({ ...r, type: 'care_request' })), ...consultReqs]
      .sort((a, b) => new Date(b.requestedAt || b.createdAt) - new Date(a.requestedAt || a.createdAt));
    setPendingRequests(merged);
    if (requestsRes.status === 'rejected' && consultsRes.status === 'rejected') {
      setErrors(prev => ({ ...prev, requests: true }));
    }

    // Kick off AI summaries + trend data for each priority patient (non-blocking)
    if (priorityRes.status === 'fulfilled' && priorityRes.value?.data?.data) {
      const patients = priorityRes.value.data.data;
      patients.forEach(p => {
        const patientId = p.patient?._id;
        if (!patientId) return;
        // Summary
        setAiSummaries(prev => ({ ...prev, [patientId]: { summary: null, loading: true } }));
        getOrFetch(`patient-summary:${patientId}`, () => api.get(`/ai/patient-summary/${patientId}`).then(r => r.data.summary), AI_TTL.PATIENT_SUMMARY)
          .then(summary => setAiSummaries(prev => ({ ...prev, [patientId]: { summary, loading: false } })))
          .catch(() => setAiSummaries(prev => ({ ...prev, [patientId]: { summary: null, loading: false } })));
        // Trend sparkline
        getOrFetch(`risk-trend:${patientId}`, () => api.get(`/ai/risk-trend/${patientId}`).then(r => r.data.data || []), AI_TTL.RISK_TREND)
          .then(data => setRiskTrends(prev => ({ ...prev, [patientId]: data })))
          .catch(() => {});
      });
    }

    setLastFetchedAt(new Date());
    setIsLoading(false);
    setIsFetching(false);
  }, []);

  const openDelegateModal = async (card) => {
    setDelegateModal(card);
    setSelectedDoctorId('');
    setDelegationNote('');
    setLoadingCareTeam(true);
    try {
      const res = await api.get(`/patient-profile/${card.patientId}`);
      const profileData = res.data?.data?.profile || res.data?.data || {};
      const careTeam = [];
      if (profileData.primaryCareProvider) {
        const pcp = profileData.primaryCareProvider;
        const pcpId = pcp._id || pcp;
        if (pcpId?.toString() !== user?.id && pcpId?.toString() !== user?._id) {
          careTeam.push({ _id: pcpId, name: pcp.name || 'Primary Care Provider', role: 'Primary' });
        }
      }
      (profileData.careTeam || []).forEach(m => {
        const doc = m.providerId;
        const docId = doc?._id || doc;
        if (docId?.toString() !== user?.id && docId?.toString() !== user?._id) {
          careTeam.push({ _id: docId, name: doc?.name || 'Doctor', role: m.role || 'Specialist' });
        }
      });
      setCareTeamDoctors(careTeam);
    } catch {
      toast.error('Could not load care team');
      setDelegateModal(null);
    } finally {
      setLoadingCareTeam(false);
    }
  };

  const handleDelegate = async () => {
    if (!selectedDoctorId || !delegateModal) return;
    setDelegating(true);
    try {
      const res = await api.post(`/risk-assessments/${delegateModal.assessmentId}/delegate-action`, {
        actionType: delegateModal.actionType,
        patientId: delegateModal.patientId,
        targetDoctorId: selectedDoctorId,
        note: delegationNote.trim() || undefined,
      });
      const toName = res.data?.data?.delegatedTo?.name || 'colleague';
      toast.success(`Action delegated to Dr. ${toName}`);
      setDelegateModal(null);
      setDismissedCards(prev => [...prev, delegateModal.originalIndex]);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to delegate action');
    } finally {
      setDelegating(false);
    }
  };

  // 1. Initial fetch
  useEffect(() => { fetchData(); }, [fetchData]);

  // 2. Auto-refresh every 3 minutes (silent — no loading flash)
  useEffect(() => {
    autoRefreshRef.current = setInterval(() => fetchData({ silent: true }), AUTO_REFRESH_MS);
    return () => clearInterval(autoRefreshRef.current);
  }, [fetchData]);

  // 3. WebSocket: instant update when a new symptom or care event arrives
  useEffect(() => {
    const onNewSymptom = () => fetchData({ silent: true });
    const onNewCareEvent = () => fetchData({ silent: true });
    webSocketEventBus.on('newSymptom', onNewSymptom);
    webSocketEventBus.on('newCareEvent', onNewCareEvent);
    webSocketEventBus.on('new_patient_report', onNewSymptom);
    return () => {
      webSocketEventBus.off('newSymptom', onNewSymptom);
      webSocketEventBus.off('newCareEvent', onNewCareEvent);
      webSocketEventBus.off('new_patient_report', onNewSymptom);
    };
  }, [fetchData]);

  // 4. Tick every 30s to keep "Updated X min ago" label fresh
  useEffect(() => {
    tickRef.current = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(tickRef.current);
  }, []);

  const handleExecuteAction = async (assessmentId, actionType, patientId, cardIndex) => {
    setExecutingAction(cardIndex);
    try {
      await api.post(`/risk-assessments/${assessmentId}/execute-action`, { actionType, patientId });
      toast.success('Action executed — care event logged');
      // Show stabilization tracker instead of dismissing
      const card = actionQueue.find(c => c.originalIndex === cardIndex);
      setStabilizingCards(prev => ({ ...prev, [cardIndex]: {
        patientName: card?.patient?.name || 'Patient',
        actionLabel: card?.action || actionType,
        patientId,
        executedAt: new Date(),
      }}));
      fetchData({ silent: true });
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to execute action');
    } finally {
      setExecutingAction(null);
    }
  };

  const handleDismissAction = async (assessmentId, actionType, patientId, cardIndex) => {
    setExecutingAction(cardIndex);
    try {
      await api.post(`/risk-assessments/${assessmentId}/dismiss-action`, {
        actionType, patientId, reason: 'Clinician dismissed from action queue'
      });
      toast.success('Action dismissed');
      setDismissedCards(prev => [...prev, cardIndex]);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to dismiss action');
    } finally {
      setExecutingAction(null);
    }
  };

  // Build action queue — memoized so it only rebuilds when inputs change.
  // Must be declared before any early returns to satisfy the Rules of Hooks.
  const actionQueue = useMemo(() => priorityPatients
    .map((p, idx) => ({ p, idx }))
    .filter(({ idx }) => !dismissedCards.includes(idx))
    .slice(0, 3).map(({ p, idx }) => {
      const riskScore = p.riskScore?.overall || 0;
      const assessment = p.assessment;
      const topFactor = assessment?.riskFactors?.[0];
      const now = Date.now();
      const topAction = assessment?.recommendedActions?.find(a =>
        (!a.status || a.status === 'pending') &&
        (!a.snoozedUntil || new Date(a.snoozedUntil).getTime() <= now)
      ) || assessment?.recommendedActions?.[0];
      const age = p.patient?.dateOfBirth
        ? Math.floor((Date.now() - new Date(p.patient.dateOfBirth).getTime()) / (365.25 * 86400000))
        : null;
      let severity = 'observation';
      if (riskScore >= 70) severity = 'critical';
      else if (riskScore >= 45) severity = 'warning';

      const actionMap = {
        urgent_clinical_review:    { label: 'Order urgent home visit',       icon: ClipboardList },
        schedule_home_visit:       { label: 'Schedule home visit',            icon: HomeIcon },
        pharmacy_outreach:         { label: 'Call Patient / Caretaker',       icon: Phone },
        care_coordinator_outreach: { label: 'Care coordinator outreach',      icon: UserPlus },
        self_care_guidance:        { label: 'Review patient data',            icon: FileText },
        schedule_virtual_visit:    { label: 'Schedule virtual visit',         icon: Stethoscope },
        medication_review:         { label: 'Medication review',              icon: Heart },
        emergency_referral:        { label: 'Emergency referral',             icon: AlertTriangle },
        follow_up_scheduling:      { label: 'Schedule follow-up',             icon: ClipboardList },
        caregiver_notification:    { label: 'Notify caregiver',               icon: Phone },
      };
      const actionInfo = actionMap[topAction?.actionType] || { label: 'Review patient data', icon: FileText };

      const snapshot = assessment?.patientSnapshot || {};
      // Prefer profile-level trend (currentRisk.trend) as it is always up-to-date;
      // fall back to assessment trend direction for backwards compatibility.
      const trend = p.riskScore?.trend || assessment?.trendAnalysis?.direction || assessment?.trend || 'stable';

      return {
        patient: { name: p.patient?.name || 'Patient', id: (p.patient?._id || '').slice(-4).toUpperCase() || '0000', age: age || '—' },
        severity, reason: topFactor?.factor || `Risk Score: ${riskScore}`,
        description: topFactor?.description || (trend === 'up' ? 'Patient condition trending upward.' : 'Patient requires monitoring.'),
        action: actionInfo.label, actionIcon: actionInfo.icon,
        assessmentId: assessment?._id, actionType: topAction?.actionType || 'self_care_guidance',
        patientId: p.patient?._id, originalIndex: idx,
        // dueWithin is stored in hours; convert to minutes for getSlaInfo
        slaMinutes: topAction?.dueWithin ? topAction.dueWithin * 60 : null,
        assessmentCreatedAt: assessment?.createdAt || null,
        // Patient context for "Why this patient?" summary
        context: {
          conditions: snapshot.activeConditions || [],
          medCount: snapshot.activeMedications || 0,
          daysSinceDischarge: snapshot.daysSinceDischarge,
          hasCaregiver: snapshot.hasCaregiver,
          events24h: snapshot.recentEvents?.last24h || 0,
          trend,
          riskScore,
        },
      };
    }), [priorityPatients, dismissedCards]);

  if (isLoading) {
    return (
      <div className="space-y-8 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-36 rounded-2xl animate-pulse bg-slate-100" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="h-64 rounded-2xl animate-pulse bg-slate-100" />
          <div className="h-64 rounded-2xl animate-pulse bg-slate-100" />
        </div>
      </div>
    );
  }

  // Stats
  const totalPatients = stats?.totalPatients || priorityPatients.length || 0;
  const highRiskCount = priorityPatients.filter(p => (p.riskScore?.overall || 0) >= 70).length;
  const moderateCount = priorityPatients.filter(p => { const s = p.riskScore?.overall || 0; return s >= 40 && s < 70; }).length;
  const lowRiskCount = Math.max(0, totalPatients - highRiskCount - moderateCount);

  // Medication adherence — patientSnapshot.activeMedications is a Number (not an array)
  const patientsWithMeds = priorityPatients.filter(p => (p.assessment?.patientSnapshot?.activeMedications || 0) > 0);
  let actualAdherenceRate = null;
  if (patientsWithMeds.length > 0) {
    const avgMedRisk = patientsWithMeds.reduce((sum, p) =>
      sum + (p.assessment?.scores?.medicationAdherenceRisk?.value || 0), 0) / patientsWithMeds.length;
    actualAdherenceRate = Math.round(100 - avgMedRisk);
  }

  // Severity breakdown
  const severityData = stats?.severityBreakdown || {};
  const trendCategories = [
    { key: 'low', label: 'Low', color: '#00696b', count: severityData.low || 0 },
    { key: 'medium', label: 'Medium', color: '#5f4300', count: severityData.medium || 0 },
    { key: 'high', label: 'High', color: '#F97316', count: severityData.high || 0 },
    { key: 'urgent', label: 'Urgent', color: '#ba1a1a', count: severityData.urgent || 0 },
  ];

  // Care event breakdown — covers all eventType enum values from the CareEvent model
  const eventTypeLabels = {
    // Patient-initiated
    symptom_reported:      'Symptom Reported',
    medication_issue:      'Medication Issue',
    appointment_missed:    'Appointment Missed',
    service_missed:        'Service Missed',
    patient_distress:      'Patient Distress',
    custom_report:         'Custom Report',
    mood_checkin:          'Mood Check-in',
    mood_check_in:         'Mood Check-in',
    // System-detected
    medication_missed:     'Medication Missed',
    medication_picked_up:  'Medication Picked Up',
    medication_taken:      'Medication Taken',
    appointment_scheduled: 'Appointment Scheduled',
    appointment_completed: 'Appointment Completed',
    appointment_attended:  'Appointment Attended',
    care_transition:       'Care Transition',
    vital_threshold:       'Vital Sign Alert',
    inactivity_detected:   'Inactivity Detected',
    // Clinical
    doctor_response:       'Doctor Response',
    care_plan_updated:     'Care Plan Updated',
    risk_assessment:       'Risk Assessment',
    // Integration
    emr_sync:              'Medical Record Sync',
    pharmacy_sync:         'Pharmacy Sync',
    device_reading:        'Device Reading',
    // Automated
    caregiver_alert:       'Caregiver Alert',
    system_signal:         'System Alert',
    // Action lifecycle
    action_generated:      'Action Generated',
    action_executed:       'Action Executed',
    action_dismissed:      'Action Dismissed',
    action_delegated:      'Action Delegated',
    action_response:       'Patient Response',
    // Consent & care team
    data_consent_granted:  'Consent Granted',
    data_consent_revoked:  'Consent Revoked',
    left_care_team:        'Left Care Team',
  };
  const eventTypes = {};
  recentCareEvents.forEach(e => {
    const raw = e.eventType || 'other';
    const t = eventTypeLabels[raw] || raw.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    eventTypes[t] = (eventTypes[t] || 0) + 1;
  });
  const topEventTypes = Object.entries(eventTypes).sort((a, b) => b[1] - a[1]).slice(0, 5);

  const worseningCount = priorityPatients.filter(p =>
    p.assessment?.trendAnalysis?.direction === 'up' || p.assessment?.trend === 'worsening' || p.assessment?.trend === 'critical'
  ).length;
  const improvingCount = priorityPatients.filter(p =>
    p.assessment?.trendAnalysis?.direction === 'down' || p.assessment?.trend === 'improving'
  ).length;

  const severityConfig = {
    critical: { badge: 'CRITICAL', badgeCls: 'bg-error/10 text-error border-error/10' },
    warning: { badge: 'WARNING', badgeCls: 'bg-amber-100 text-amber-700 border-amber-200' },
    observation: { badge: 'OBSERVATION', badgeCls: 'bg-sky-100 text-sky-700 border-sky-200' },
  };

  const getSlaInfo = (createdAt, dueIn) => {
    if (!createdAt || !dueIn) return null;
    const deadline = new Date(createdAt).getTime() + dueIn * 60 * 1000;
    const remaining = deadline - Date.now();
    if (remaining <= 0) return { label: 'OVERDUE', cls: 'bg-red-100 text-red-700 border-red-200', urgent: true };
    const h = Math.floor(remaining / 3600000);
    const m = Math.floor((remaining % 3600000) / 60000);
    const label = h > 0 ? `${h}h ${m}m left` : `${m}m left`;
    const urgent = remaining < 30 * 60 * 1000;
    return { label, cls: urgent ? 'bg-red-50 text-red-700 border-red-200' : 'bg-amber-50 text-amber-700 border-amber-200', urgent };
  };

  return (
    <div className="space-y-8 py-6">
      {/* Silent fetch indicator */}
      {isFetching && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2 px-3 py-2 bg-white rounded-full shadow-lg border border-outline-variant/10 text-xs font-semibold text-slate-500">
          <RefreshCw className="w-3 h-3 animate-spin text-primary" />
          Syncing…
        </div>
      )}
      {/* Per-widget error banner — only shown if ALL widgets fail */}
      {Object.values(errors).every(Boolean) && (
        <div className="p-4 rounded-2xl bg-error/5 border border-error/10">
          <p className="text-error text-sm font-medium">Could not load dashboard data.</p>
          <button onClick={() => fetchData()} className="mt-2 px-4 py-2 bg-slate-900 text-white text-xs font-bold rounded-xl hover:bg-primary transition-all">Retry</button>
        </div>
      )}

      <OnCallStatusWidget />

      {/* ── Hero: urgency summary ── */}
      {(highRiskCount > 0 || delegatedActions.length > 0) ? (
        <div className="rounded-2xl bg-error/5 border border-error/10 px-6 py-5 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-error/10 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5 text-error" />
            </div>
            <div>
              <p className="font-headline text-xl font-bold text-slate-900">
                {highRiskCount + delegatedActions.length} patient{(highRiskCount + delegatedActions.length) !== 1 ? 's' : ''} need attention now
              </p>
              <p className="text-sm text-slate-500 mt-0.5">
                {highRiskCount > 0 && <span className="text-error font-semibold">{highRiskCount} critical</span>}
                {highRiskCount > 0 && moderateCount > 0 && ' · '}
                {moderateCount > 0 && <span className="text-amber-600 font-semibold">{moderateCount} moderate</span>}
                {delegatedActions.length > 0 && (highRiskCount > 0 || moderateCount > 0) && ' · '}
                {delegatedActions.length > 0 && <span className="font-semibold">{delegatedActions.length} delegated</span>}
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/priority-queue')}
            className="flex items-center gap-2 px-5 py-2.5 bg-error text-white rounded-xl font-bold text-sm hover:bg-error/90 active:scale-95 transition-all"
          >
            Full Priority View <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      ) : !isLoading && (
        <div className="rounded-2xl bg-emerald-50 border border-emerald-200 px-6 py-4 flex items-center gap-4">
          <CheckCircle className="w-8 h-8 text-emerald-600 shrink-0" />
          <p className="font-semibold text-emerald-900">All clear — no patients require immediate attention.</p>
        </div>
      )}

      {/* ── Tab navigation ── */}
      <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-xl w-fit">
        <button
          onClick={() => setActiveTab('queue')}
          className={`px-5 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'queue' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
        >
          Priority Queue {actionQueue.length > 0 && <span className="ml-1.5 px-2 py-0.5 rounded-full bg-error text-white text-xs">{actionQueue.length}</span>}
        </button>
        <button
          onClick={() => setActiveTab('insights')}
          className={`px-5 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'insights' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
        >
          Insights
        </button>
      </div>

      {/* ── Priority Queue tab ── */}
      {activeTab === 'queue' && <>

      {/* ── Today's Schedule ─────────────────────────────── */}
      {appointments.length > 0 && (() => {
        const APPT_CFG = {
          virtual_visit:      { label: 'Virtual',    Icon: Video,     color: 'text-blue-600',   bg: 'bg-blue-50',   badge: 'bg-blue-100 text-blue-700' },
          home_visit:         { label: 'Home Visit', Icon: HomeIcon,  color: 'text-teal-600',   bg: 'bg-teal-50',   badge: 'bg-teal-100 text-teal-700' },
          pharmacist_consult: { label: 'Pharmacy',   Icon: Pill,      color: 'text-purple-600', bg: 'bg-purple-50', badge: 'bg-purple-100 text-purple-700' },
        };
        const todayStart = new Date(); todayStart.setHours(0,0,0,0);
        const todayEnd   = new Date(); todayEnd.setHours(23,59,59,999);
        const todayAppts = appointments.filter(a => {
          const d = new Date(a.appointmentData?.scheduledDate);
          return d >= todayStart && d <= todayEnd;
        });
        const upcomingAppts = appointments.filter(a => {
          const d = new Date(a.appointmentData?.scheduledDate);
          return d > todayEnd;
        }).slice(0, 3);

        return (
          <section aria-label="Today's schedule">
            <div className="flex items-center gap-2 mb-3">
              <CalendarClock className="w-5 h-5 text-primary" />
              <h2 className="font-headline text-xl font-bold text-slate-900">Today's Schedule</h2>
              {todayAppts.length > 0 && (
                <span className="ml-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">{todayAppts.length} today</span>
              )}
            </div>
            <div className="space-y-2">
              {[...todayAppts, ...upcomingAppts].map((appt) => {
                const type = appt.appointmentData?.appointmentType;
                const cfg  = APPT_CFG[type] || APPT_CFG.virtual_visit;
                const { Icon } = cfg;
                const scheduled  = appt.appointmentData?.scheduledDate ? new Date(appt.appointmentData.scheduledDate) : null;
                const apptDay    = scheduled ? new Date(scheduled) : null;
                if (apptDay) apptDay.setHours(0,0,0,0);
                const isToday    = apptDay?.getTime() === todayStart.getTime();
                const dayLabel   = isToday ? 'Today' : scheduled?.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
                const timeLabel  = scheduled?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                const meetingLink = appt.appointmentData?.meetingLink;
                const patient    = appt.patientId;

                return (
                  <div
                    key={appt._id}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${isToday ? 'bg-blue-50 border-blue-100' : 'bg-slate-50 border-slate-100'}`}
                  >
                    <div className={`w-9 h-9 rounded-lg ${isToday ? 'bg-white' : 'bg-white'} flex items-center justify-center shrink-0 shadow-sm`}>
                      <Icon className={`w-4 h-4 ${cfg.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-900 truncate">
                        {patient?.name || 'Patient'}
                      </p>
                      <p className="text-xs text-slate-500">
                        <span className={`inline-block mr-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${cfg.badge}`}>{cfg.label}</span>
                        {dayLabel} · {timeLabel}
                      </p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {meetingLink && (
                        <a
                          href={meetingLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700 transition-colors"
                        >
                          <Video className="w-3 h-3" /> Join
                        </a>
                      )}
                      <button
                        onClick={() => navigate(`/care-event/${appt._id}`)}
                        className="px-3 py-1.5 border border-slate-200 bg-white text-slate-700 rounded-lg text-xs font-bold hover:bg-slate-50 transition-colors"
                      >
                        View
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })()}

      {/* ── Silent post-discharge warning ── */}
      {(() => {
        const silentPatients = priorityPatients.filter(p => {
          const snap = p.assessment?.patientSnapshot || {};
          const days = snap.daysSinceDischarge;
          const events24h = snap.recentEvents?.last24h ?? null;
          return typeof days === 'number' && days <= 14 && events24h === 0;
        });
        if (silentPatients.length === 0) return null;
        return (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
                <AlertTriangle className="w-4.5 h-4.5 text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-amber-900">
                  {silentPatients.length === 1
                    ? '1 patient hasn\'t checked in since discharge'
                    : `${silentPatients.length} patients haven't checked in since discharge`}
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {silentPatients.map(p => {
                    const snap = p.assessment?.patientSnapshot || {};
                    return (
                      <button
                        key={p.patient?._id}
                        onClick={() => navigate(`/patient/${p.patient?._id}`)}
                        className="flex items-center gap-1.5 px-3 py-1 bg-white border border-amber-200 rounded-full text-xs font-bold text-amber-800 hover:bg-amber-100 transition-colors"
                      >
                        {p.patient?.name}
                        <span className="opacity-60">· {snap.daysSinceDischarge}d post-discharge</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Immediate Action Queue */}
      <section className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="font-headline text-xl font-bold text-slate-900">Immediate Action Queue</h2>
          <button
            onClick={() => navigate('/priority-queue')}
            className="flex items-center gap-2 px-4 py-1.5 bg-white border border-outline-variant/30 rounded-full text-xs font-bold text-slate-600 uppercase tracking-widest hover:bg-slate-50 transition-colors"
          >
            <Filter className="w-3.5 h-3.5" />
            Priority View
          </button>
        </div>
        {actionQueue.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {actionQueue.map((card, i) => {
              const cfg = severityConfig[card.severity] || severityConfig.observation;
              const ActionIcon = card.actionIcon;
              const ctx = card.context || {};
              const TrendIcon = ctx.trend === 'up' || ctx.trend === 'worsening' || ctx.trend === 'critical' ? TrendingUp
                : ctx.trend === 'down' || ctx.trend === 'improving' ? TrendingDown : Minus;
              const trendColor = ctx.trend === 'up' || ctx.trend === 'worsening' || ctx.trend === 'critical' ? 'text-red-500'
                : ctx.trend === 'down' || ctx.trend === 'improving' ? 'text-emerald-500' : 'text-slate-500';
              const trendLabel = { improving: '↓', stable: '→', worsening: '↑', critical: '↑↑' }[ctx.trend] || '→';
              const stabilization = stabilizingCards[card.originalIndex];
              const slaInfo = getSlaInfo(card.assessmentCreatedAt, card.slaMinutes);

              // Stabilization tracker replaces the action card after execution
              if (stabilization) {
                return (
                  <div key={i} className="bg-emerald-50 rounded-2xl border border-emerald-200 p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest border bg-emerald-100 text-emerald-700 border-emerald-200">TRACKING</span>
                      <button
                        onClick={() => { setStabilizingCards(prev => { const n = {...prev}; delete n[card.originalIndex]; return n; }); setDismissedCards(prev => [...prev, card.originalIndex]); }}
                        className="text-xs font-bold text-slate-500 hover:text-slate-700"
                        aria-label="Dismiss stabilization tracker"
                      >Dismiss</button>
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-emerald-900">{stabilization.patientName}</h3>
                      <p className="text-sm text-emerald-700 mt-1">
                        <CheckCircle className="w-4 h-4 inline mr-1" aria-hidden="true" />
                        {stabilization.actionLabel} — executed at {stabilization.executedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    <div className="bg-white/60 rounded-xl p-3">
                      <div className="flex items-center gap-2 text-xs font-bold text-emerald-600">
                        <Clock className="w-3.5 h-3.5" aria-hidden="true" />
                        Monitoring for stabilization — check back in 2–4 hours
                      </div>
                      <div className="w-full bg-emerald-200 rounded-full h-1.5 mt-2">
                        <div className="bg-emerald-500 h-1.5 rounded-full w-1/6 transition-all" />
                      </div>
                    </div>
                    <button
                      onClick={() => navigate(`/patient/${stabilization.patientId}`)}
                      className="w-full flex items-center justify-center gap-2 py-3 bg-emerald-600 text-white rounded-xl font-bold text-sm hover:bg-emerald-700 active:scale-95 transition-all"
                    >
                      View Patient <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                );
              }

              return (
                <div key={i} className="bg-white rounded-2xl shadow-premium border border-outline-variant/10 p-6 space-y-4">
                  {/* Header: severity badge + SLA countdown + patient ID */}
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest border ${cfg.badgeCls}`}>{cfg.badge}</span>
                      {slaInfo && (
                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold border flex items-center gap-1 ${slaInfo.cls}`}>
                          <Clock className="w-3 h-3" aria-hidden="true" />
                          {slaInfo.label}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <TrendIcon className={`w-3.5 h-3.5 ${trendColor}`} aria-hidden="true" />
                      <span className={`text-xs font-bold ${trendColor}`}>{trendLabel}</span>
                      <span className="text-xs font-bold text-slate-400 ml-1">#{card.patient.id}</span>
                    </div>
                  </div>

                  {/* Patient name + sparkline */}
                  <div className="flex items-start justify-between gap-2">
                    <button
                      onClick={() => navigate(`/patient/${card.patientId}`)}
                      className="text-left group/name"
                    >
                      <h3 className="font-bold text-lg text-slate-900 group-hover/name:text-primary transition-colors">
                        {card.patient.name}
                        {ctx.riskScore ? <span className="text-slate-400 font-semibold text-sm ml-2">#{ctx.riskScore}</span> : ''}
                      </h3>
                    </button>
                    {riskTrends[card.patientId]?.length > 1 && (
                      <div className="flex flex-col items-end gap-0.5 shrink-0">
                        <Sparkline points={riskTrends[card.patientId]} />
                        <span className="text-[9px] text-slate-400 font-medium">14-day risk</span>
                      </div>
                    )}
                  </div>

                  {/* AI clinical summary */}
                  {(() => {
                    const ai = aiSummaries[card.patientId];
                    if (!ai) return null;
                    if (ai.loading) return (
                      <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-xl border border-slate-100">
                        <div className="w-3 h-3 rounded-full border-2 border-primary border-t-transparent animate-spin shrink-0" />
                        <span className="text-xs text-slate-400">Generating clinical summary…</span>
                      </div>
                    );
                    if (!ai.summary) return null;
                    return (
                      <div className="px-3 py-2.5 bg-blue-50 border border-blue-100 rounded-xl">
                        <p className="text-[10px] font-bold text-blue-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                          <Zap className="w-3 h-3" /> AI Summary
                        </p>
                        <p className="text-xs text-slate-700 leading-relaxed">{ai.summary}</p>
                      </div>
                    );
                  })()}

                  {/* Context summary — "Why does this patient need attention?" */}
                  <div className="rounded-xl border border-slate-100 overflow-hidden">
                    {/* "Why now" row */}
                    <div className="px-3 py-2.5 bg-slate-50 border-b border-slate-100">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Why now</p>
                      <p className="text-sm text-slate-800 font-semibold leading-snug">{card.description || card.reason}</p>
                    </div>
                    {/* Conditions row */}
                    {ctx.conditions?.length > 0 && (
                      <div className="px-3 py-2 bg-white border-b border-slate-100">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Known conditions</p>
                        <div className="flex flex-wrap gap-1">
                          {ctx.conditions.slice(0, 3).map((c, ci) => (
                            <span key={ci} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs font-semibold">{c}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Signals strip */}
                    <div className="px-3 py-2 bg-white flex flex-wrap gap-x-3 gap-y-1 text-xs">
                      {card.patient.age !== '—' && <span className="text-slate-500">{card.patient.age}y old</span>}
                      {ctx.medCount > 0 && <span className="text-slate-500">{ctx.medCount} active meds</span>}
                      {ctx.daysSinceDischarge != null && (
                        <span className={ctx.daysSinceDischarge <= 14 ? 'text-amber-700 font-bold' : 'text-slate-500'}>
                          {ctx.daysSinceDischarge}d post-discharge
                        </span>
                      )}
                      {ctx.events24h > 0 && (
                        <span className="text-red-600 font-bold">{ctx.events24h} events in 24h</span>
                      )}
                      {!ctx.hasCaregiver && <span className="text-amber-700 font-bold">No caregiver</span>}
                    </div>
                  </div>

                  {/* Primary action */}
                  <button
                    disabled={executingAction === card.originalIndex}
                    onClick={() => {
                      if (card.actionType === 'follow_up_scheduling') {
                        setFollowUpModal({ patientId: card.patientId, assessmentId: card.assessmentId, actionType: card.actionType, cardIndex: card.originalIndex });
                      } else {
                        handleExecuteAction(card.assessmentId, card.actionType, card.patientId, card.originalIndex);
                      }
                    }}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-white rounded-xl font-bold text-sm hover:bg-primary/90 active:scale-95 transition-all disabled:opacity-50"
                  >
                    <ActionIcon className="w-4 h-4" />
                    {executingAction === card.originalIndex ? 'Executing...' : card.action}
                  </button>

                  {/* Secondary actions: Snooze · Delegate · Dismiss */}
                  <div className="flex gap-2">
                    {/* Snooze — dropdown for duration, persisted to backend */}
                    <div className="flex-1 relative">
                      <select
                        disabled={snoozingCard === card.originalIndex}
                        defaultValue=""
                        onChange={async (e) => {
                          const hours = Number(e.target.value);
                          if (!hours) return;
                          e.target.value = '';
                          setSnoozingCard(card.originalIndex);
                          try {
                            await api.post(`/risk-assessments/${card.assessmentId}/snooze-action`, {
                              actionType: card.actionType,
                              patientId: card.patientId,
                              hours,
                            });
                            toast.success(`Snoozed for ${hours}h`);
                            setDismissedCards(prev => [...prev, card.originalIndex]);
                          } catch {
                            toast.error('Failed to snooze');
                          } finally {
                            setSnoozingCard(null);
                          }
                        }}
                        className="w-full py-2.5 pl-3 pr-8 border border-slate-200 text-slate-500 rounded-xl text-xs font-bold hover:bg-slate-50 transition-all bg-white appearance-none cursor-pointer disabled:opacity-50"
                      >
                        <option value="" disabled>Snooze…</option>
                        <option value="1">Snooze 1h</option>
                        <option value="2">Snooze 2h</option>
                        <option value="4">Snooze 4h</option>
                        <option value="8">Snooze 8h</option>
                        <option value="24">Snooze 24h</option>
                      </select>
                      <Clock className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                    </div>
                    <button
                      onClick={() => openDelegateModal(card)}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2.5 border border-slate-200 text-slate-500 rounded-xl text-xs font-bold hover:bg-slate-50 transition-all"
                    >
                      <Users className="w-3.5 h-3.5" /> Delegate
                    </button>
                    <button
                      disabled={executingAction === card.originalIndex}
                      onClick={() => handleDismissAction(card.assessmentId, card.actionType, card.patientId, card.originalIndex)}
                      className="flex-1 py-2.5 border border-slate-200 text-slate-500 rounded-xl text-xs font-bold hover:bg-slate-50 transition-all disabled:opacity-50"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : errors.priority ? (
          <div className="bg-white rounded-2xl border border-red-100 p-8 text-center">
            <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-red-400" />
            <h3 className="font-bold text-slate-900 mb-1">Could not load priority queue</h3>
            <p className="text-sm text-slate-500 mb-4">Check your connection and try again.</p>
            <button onClick={() => fetchData()} className="px-4 py-2 bg-slate-900 text-white text-xs font-bold rounded-xl hover:bg-primary transition-all">Retry</button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-premium border border-outline-variant/10 p-10 text-center">
            <CheckCircle className="w-12 h-12 mx-auto mb-3 text-secondary" />
            <h3 className="font-headline font-bold text-lg text-slate-900 mb-1">All Clear</h3>
            <p className="text-sm text-slate-500 mb-3">No priority actions required at this time.</p>
            {recentCareEvents.length > 0 && (() => {
              const PATIENT_EVENTS = new Set(['symptom_reported','medication_issue','medication_missed','appointment_missed','service_missed','patient_distress','custom_report','mood_checkin','mood_check_in','vital_threshold']);
              const last = recentCareEvents.find(e => PATIENT_EVENTS.has(e.eventType));
              if (!last) return null;
              const lastLabel = eventTypeLabels[last?.eventType] || last?.eventType?.replace(/_/g, ' ') || '—';
              const lastTime = last?.timestamp || last?.createdAt;
              return (
                <p className="text-xs text-slate-400 font-medium">
                  Last event: <span className="text-slate-600 font-semibold">{lastLabel}</span>
                  {lastTime && <> · {getRelativeTime(new Date(lastTime))}</>}
                </p>
              );
            })()}
          </div>
        )}
      </section>

      {/* ── New Patient Reports ── patient-initiated events only, no system noise */}
      {(() => {
        const PATIENT_EVENTS = new Set([
          'symptom_reported', 'medication_issue', 'medication_missed',
          'appointment_missed', 'service_missed', 'patient_distress',
          'custom_report', 'mood_checkin', 'mood_check_in', 'vital_threshold',
        ]);
        const patientReports = recentCareEvents.filter(e => PATIENT_EVENTS.has(e.eventType));
        if (patientReports.length === 0) return null;
        const newCount = patientReports.filter(e => {
          const ts = e.timestamp || e.createdAt;
          return ts && new Date(ts) > lastSeenAt;
        }).length;
        return (
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-primary" />
                <h2 className="font-headline text-xl font-bold text-slate-900">New Patient Reports</h2>
                <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-xs font-bold">{patientReports.length}</span>
                {newCount > 0 && (
                  <span className="px-2 py-0.5 rounded-full bg-primary text-white text-xs font-extrabold">
                    {newCount} new
                  </span>
                )}
              </div>
              <button onClick={() => navigate('/health-log')} className="text-xs font-bold text-primary flex items-center gap-1 hover:gap-2 transition-all">
                Full Log <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {patientReports.slice(0, 10).map((event, i) => {
                const label = eventTypeLabels[event.eventType] || event.eventType?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Event';
                const sev = event.severity || 'medium';
                const dotCls = { critical: 'bg-red-500', high: 'bg-orange-400', medium: 'bg-amber-400', low: 'bg-emerald-400' }[sev] || 'bg-slate-300';
                const ts = event.timestamp || event.createdAt;
                const isNew = ts && new Date(ts) > lastSeenAt;
                return (
                  <button
                    key={event._id || i}
                    onClick={() => navigate(`/care-event/${event._id}`)}
                    className={`w-full text-left bg-white border border-slate-100 rounded-xl p-4 hover:shadow-md transition-all active:scale-[0.99] ${isNew ? 'ring-1 ring-primary/20' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`w-2 h-2 rounded-full shrink-0 mt-0.5 ${dotCls}`} />
                        <p className="text-sm font-bold text-slate-900 leading-tight truncate">{label}</p>
                      </div>
                      {isNew && (
                        <span className="shrink-0 px-1.5 py-0.5 text-[10px] font-extrabold bg-primary text-white rounded-full uppercase tracking-wide">New</span>
                      )}
                    </div>
                    <p className="text-xs font-semibold text-slate-600">{event.patientId?.name || 'Patient'}</p>
                    {event.data?.details && (
                      <p className="text-xs text-slate-400 mt-1 line-clamp-2">{event.data.details}</p>
                    )}
                    <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-50">
                      <span className="text-xs text-slate-400">{ts ? getRelativeTime(new Date(ts)) : ''}</span>
                      <span className="text-xs font-bold text-primary flex items-center gap-1">Review <ChevronRight className="w-3 h-3" /></span>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        );
      })()}

      {/* ── Pending Care Requests ── patients waiting for doctor to accept */}
      {!errors.requests && pendingRequests.length > 0 && (() => {
        const q = requestSearch.trim().toLowerCase();
        const filtered = q
          ? pendingRequests.filter(r => {
              const name = (r.type === 'consult' ? r.patient?.name : r.patientId?.name) || '';
              const title = r.symptomTitle || '';
              return name.toLowerCase().includes(q) || title.toLowerCase().includes(q);
            })
          : pendingRequests;
        const visible = filtered.slice(0, 3);
        return (
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-primary" />
                <h2 className="font-headline text-xl font-bold text-slate-900">Pending Requests</h2>
                <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">{pendingRequests.length}</span>
              </div>
              <button onClick={() => navigate('/care-requests')} className="text-xs font-bold text-primary flex items-center gap-1 hover:gap-2 transition-all">
                Manage all <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search by patient name…"
                value={requestSearch}
                onChange={e => setRequestSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
              />
            </div>

            {visible.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No requests match "{requestSearch}"</p>
            ) : (
              <div className="space-y-2">
                {visible.map(req => {
                  const isConsult = req.type === 'consult';
                  const patient = isConsult ? req.patient : (req.patientId || {});
                  const name = patient?.name || 'Unknown patient';
                  const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
                  const ts = req.requestedAt || req.createdAt;
                  const badgeLabel = isConsult ? 'Consult' : ({ primary: 'Care team', specialist: 'Specialist', consulting: 'Consulting' }[req.requestedRole] || 'Care team');
                  const badgeCls = isConsult ? 'bg-violet-100 text-violet-700' : 'bg-primary/10 text-primary';
                  const note = isConsult ? req.note : req.message;
                  const destination = isConsult ? `/symptom/${req.symptomId}` : '/care-requests';
                  return (
                    <div key={req._id} className="bg-white border border-slate-100 rounded-xl p-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary font-extrabold text-sm shrink-0">
                        {initials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-bold text-slate-900 truncate">{name}</p>
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${badgeCls}`}>{badgeLabel}</span>
                        </div>
                        {isConsult && req.symptomTitle && (
                          <p className="text-xs text-slate-600 mt-0.5 truncate font-medium">{req.symptomTitle}</p>
                        )}
                        {note && (
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{note}</p>
                        )}
                        {ts && (
                          <p className="text-[10px] text-slate-400 mt-0.5">{getRelativeTime(new Date(ts))}</p>
                        )}
                      </div>
                      {isConsult ? (
                        <div className="flex gap-1.5 shrink-0">
                          <button
                            disabled={consultAction?.symptomId === req.symptomId?.toString()}
                            onClick={async () => {
                              const sid = req.symptomId?.toString();
                              setConsultAction({ symptomId: sid, action: 'accept' });
                              try {
                                await api.post(`/responses/${sid}/consult/accept`);
                                setPendingRequests(prev => prev.filter(r => r.symptomId?.toString() !== sid));
                                navigate(`/symptom/${sid}`);
                              } catch {
                                toast.error('Could not accept consult');
                              } finally {
                                setConsultAction(null);
                              }
                            }}
                            className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-bold rounded-lg hover:bg-emerald-700 active:scale-95 transition-all disabled:opacity-50"
                          >
                            {consultAction?.symptomId === req.symptomId?.toString() && consultAction.action === 'accept' ? '…' : 'Accept'}
                          </button>
                          <button
                            disabled={consultAction?.symptomId === req.symptomId?.toString()}
                            onClick={async () => {
                              const sid = req.symptomId?.toString();
                              setConsultAction({ symptomId: sid, action: 'decline' });
                              try {
                                await api.post(`/responses/${sid}/consult/decline`);
                                setPendingRequests(prev => prev.filter(r => r.symptomId?.toString() !== sid));
                              } catch {
                                toast.error('Could not decline consult');
                              } finally {
                                setConsultAction(null);
                              }
                            }}
                            className="px-3 py-1.5 bg-slate-100 text-slate-700 text-xs font-bold rounded-lg hover:bg-slate-200 active:scale-95 transition-all disabled:opacity-50"
                          >
                            {consultAction?.symptomId === req.symptomId?.toString() && consultAction.action === 'decline' ? '…' : 'Decline'}
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => navigate(destination)}
                          className="shrink-0 px-3 py-1.5 bg-primary text-white text-xs font-bold rounded-lg hover:bg-primary/90 active:scale-95 transition-all"
                        >
                          Review
                        </button>
                      )}
                    </div>
                  );
                })}
                {filtered.length > 3 && (
                  <button onClick={() => navigate('/care-requests')} className="w-full py-2 text-xs font-bold text-primary hover:underline">
                    +{filtered.length - 3} more — view all
                  </button>
                )}
              </div>
            )}
          </section>
        );
      })()}

      {/* Delegated Actions — actions assigned to this doctor by colleagues */}
      {errors.delegated && (
        <div className="rounded-2xl border border-amber-100 bg-amber-50 px-5 py-4 flex items-center gap-3 text-sm text-amber-800">
          <AlertTriangle className="w-4 h-4 shrink-0 text-amber-600" />
          <span className="font-medium">Could not load delegated actions.</span>
          <button onClick={() => fetchData()} className="ml-auto text-xs font-bold underline hover:no-underline">Retry</button>
        </div>
      )}
      {!errors.delegated && delegatedActions.length > 0 && (
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-headline text-xl font-bold text-slate-900">Delegated to You</h2>
            <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold">{delegatedActions.length}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {delegatedActions.map((action) => {
              const priorityCfg = {
                critical: 'bg-error/10 text-error border-error/20',
                high: 'bg-orange-50 text-orange-600 border-orange-200',
                medium: 'bg-amber-50 text-amber-700 border-amber-200',
                low: 'bg-slate-50 text-slate-600 border-slate-200',
              };
              const pCls = priorityCfg[action.priority] || priorityCfg.medium;
              const delegatedLabelMap = {
                urgent_clinical_review: 'Urgent Clinical Review', schedule_home_visit: 'Schedule Home Visit',
                pharmacy_outreach: 'Call Patient / Caretaker', care_coordinator_outreach: 'Care Coordinator Outreach',
                self_care_guidance: 'Review Patient Data', schedule_virtual_visit: 'Schedule Virtual Visit',
                medication_review: 'Medication Review', emergency_referral: 'Emergency Referral',
                follow_up_scheduling: 'Schedule Follow-up', caregiver_notification: 'Notify Caregiver',
              };
              const actionLabel = delegatedLabelMap[action.actionType] || action.actionType?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Review';
              return (
                <div key={action._id} className="bg-white rounded-2xl border border-outline-variant/10 shadow-sm p-5 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-bold border uppercase ${pCls}`}>{action.priority}</span>
                    <span className="text-xs text-slate-400">{action.delegatedAt ? new Date(action.delegatedAt).toLocaleDateString() : ''}</span>
                  </div>
                  <div>
                    <p className="font-bold text-sm text-slate-900 capitalize">{actionLabel}</p>
                    <p className="text-xs text-slate-500 mt-0.5">Patient: {action.patientId?.name || '—'}</p>
                    {action.delegationNote && (
                      <p className="text-xs text-slate-500 mt-1 italic">"{action.delegationNote}"</p>
                    )}
                    <p className="text-xs text-slate-400 mt-1">From Dr. {action.delegatedBy?.name || '—'}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/patient/${action.patientId?._id}`)}
                      className="flex-1 py-2 text-xs font-bold rounded-xl bg-primary text-white hover:bg-primary/90 transition-all"
                    >
                      View Patient
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          await api.post(`/risk-assessments/${action.riskAssessmentId}/execute-action`, {
                            actionType: action.actionType,
                            patientId: action.patientId?._id,
                          });
                          toast.success('Action executed');
                          setDelegatedActions(prev => prev.filter(a => a._id !== action._id));
                        } catch { toast.error('Failed to execute'); }
                      }}
                      className="flex-1 py-2 text-xs font-bold rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all"
                    >
                      Execute
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      </>}

      {/* ── Insights tab ── */}
      {activeTab === 'insights' && <>

      {/* Health Trend Insights */}
      <section className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="font-headline text-xl font-bold text-slate-900">Health Trend Insights</h2>
          <button onClick={() => navigate('/health-log')} className="text-primary text-[13px] font-bold flex items-center gap-2 hover:gap-3 transition-all">
            Full Patient Log <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Severity Distribution */}
          <div className="lg:col-span-8 bg-white rounded-3xl p-6 lg:p-8 shadow-premium border border-outline-variant/10 relative overflow-hidden flex flex-col justify-between min-h-[250px] lg:min-h-[300px]">
            <div className="flex flex-wrap gap-4 justify-between items-start z-10 w-full">
              <div>
                <p className="text-xs font-extrabold text-primary uppercase tracking-[0.2em] mb-2">Severity Distribution</p>
                <h3 className="font-headline text-3xl font-bold text-slate-900">Symptom Overview</h3>
                <p className="text-sm text-slate-500 mt-1 font-medium">Active caseload across {totalPatients} patients</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <span className="flex items-center gap-2 bg-slate-50 border border-outline-variant/20 px-4 py-2 rounded-full text-xs font-bold text-slate-600 shadow-sm">
                  <span className="w-2 h-2 rounded-full bg-emerald-500" /> {lowRiskCount} Low
                </span>
                <span className="flex items-center gap-2 bg-slate-50 border border-outline-variant/20 px-4 py-2 rounded-full text-xs font-bold text-slate-600 shadow-sm">
                  <span className="w-2 h-2 rounded-full bg-rose-500" /> {highRiskCount} High
                </span>
              </div>
            </div>
            <div className="flex items-end gap-3 h-36 w-full mt-8">
              {trendCategories.map((cat) => {
                const maxCount = Math.max(...trendCategories.map(c => c.count), 1);
                const height = Math.max((cat.count / maxCount) * 100, 8);
                return (
                  <div key={cat.key} className="flex-1 flex flex-col items-center gap-2">
                    <span className="text-sm font-bold text-slate-900">{cat.count}</span>
                    <div className="w-full rounded-t-xl transition-all hover:opacity-80" style={{ height: `${height}%`, backgroundColor: cat.color }} />
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{cat.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Care Events */}
          <div className="lg:col-span-4 bg-white rounded-3xl p-6 lg:p-8 border border-outline-variant/10 flex flex-col justify-between shadow-premium">
            <div className="space-y-4">
              <div className="w-14 h-14 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600">
                <Activity className="w-7 h-7" />
              </div>
              <div>
                <h4 className="font-headline font-bold text-xl text-slate-900">Care Events (48h)</h4>
                <p className="text-[13px] text-slate-500 leading-relaxed mt-1.5 font-medium">
                  {recentCareEvents.length} events in the last 48 hours.
                </p>
              </div>
            </div>
            <div className="space-y-2 pt-6 border-t border-slate-50">
              {topEventTypes.length > 0 ? topEventTypes.map(([type, count]) => (
                <div key={type} className="flex justify-between text-xs font-bold text-slate-500">
                  <span className="capitalize">{type}</span>
                  <span className="text-slate-900">{count}</span>
                </div>
              )) : (
                <p className="text-xs text-slate-500">No recent events</p>
              )}
            </div>
          </div>

          {/* Medication Compliance */}
          <div className="lg:col-span-4 bg-primary text-white rounded-3xl p-6 lg:p-8 flex flex-col justify-between shadow-xl shadow-primary/20 min-h-[180px] lg:min-h-[200px] overflow-hidden">
            <div className="space-y-2 min-w-0">
              <div className="text-xs font-extrabold uppercase tracking-[0.2em] opacity-70">Metric Highlight</div>
              <h4 className="font-headline font-extrabold text-2xl tracking-tight leading-tight break-words">Medication Compliance</h4>
              <p className="text-sm font-medium opacity-80 mt-1">
                {actualAdherenceRate !== null
                  ? 'Estimated adherence rate based on risk scores.'
                  : patientsWithMeds.length === 0 && totalPatients > 0
                    ? 'No elevated-risk patients on record medications.'
                    : 'No patients in your caseload yet.'}
              </p>
            </div>
            <div className="flex items-end justify-between gap-3 mt-4">
              <div className="text-5xl font-black tracking-tighter leading-none">
                {actualAdherenceRate !== null ? `${actualAdherenceRate}%` : '—'}
              </div>
              <div className="flex flex-col bg-white/10 px-3 py-2 rounded-2xl shrink-0 text-right">
                <span className="text-xs font-extrabold uppercase tracking-widest">Patients</span>
                <span className="text-[13px] font-bold">
                  {patientsWithMeds.length > 0 ? patientsWithMeds.length : totalPatients}
                  <span className="text-xs opacity-70"> {patientsWithMeds.length > 0 ? 'tracked' : 'in caseload'}</span>
                </span>
              </div>
            </div>
          </div>

          {/* Patient Risk Trends */}
          <div className="lg:col-span-8 bg-slate-50 rounded-3xl p-6 lg:p-8 flex items-center gap-6 lg:gap-10 border border-outline-variant/10 hover:bg-white transition-colors duration-500 flex-wrap">
            <div className="flex -space-x-4">
              {priorityPatients.slice(0, 3).map((p, i) => (
                <div key={i} className="w-14 h-14 rounded-2xl border-4 border-white shadow-sm ring-1 ring-slate-100 flex items-center justify-center font-extrabold text-sm" style={{ backgroundColor: ['#4F7396','#6B8E9B','#8CA3A0'][i], color: '#fff' }}>
                  {(p.patient?.name || 'P').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                </div>
              ))}
              {totalPatients > 3 && (
                <div className="w-14 h-14 rounded-2xl bg-slate-900 border-4 border-white flex items-center justify-center text-[13px] font-extrabold text-white shadow-sm">
                  +{totalPatients - 3}
                </div>
              )}
            </div>
            <div className="flex-1 space-y-1">
              <h4 className="font-headline font-bold text-xl text-slate-900">Patient Risk Trends</h4>
              <p className="text-sm text-slate-500 font-medium">
                {priorityPatients.length === 0 ? (
                  <span className="text-secondary font-semibold">All patients are at low risk — no elevated scores detected</span>
                ) : (
                  <>
                    {worseningCount > 0 && <><span className="text-error font-bold">{worseningCount}</span> worsening</>}
                    {worseningCount > 0 && improvingCount > 0 && ' · '}
                    {improvingCount > 0 && <><span className="text-secondary font-bold">{improvingCount}</span> improving</>}
                    {worseningCount === 0 && improvingCount === 0 && <><span className="text-slate-700 font-semibold">{moderateCount}</span> at moderate risk</>}
                    <span className="text-slate-400"> · {priorityPatients.length} elevated-risk patients monitored</span>
                  </>
                )}
              </p>
            </div>
            <button onClick={() => navigate('/priority-queue')} className="bg-white p-4 rounded-2xl shadow-premium hover:shadow-premium-hover hover:scale-105 transition-all text-primary border border-slate-50">
              <ArrowRight className="w-6 h-6" />
            </button>
          </div>
        </div>
      </section>

      </>}

      {/* Case Summary Footer — always visible */}

      <section className="bg-white rounded-3xl p-10 shadow-premium border border-outline-variant/10 flex items-center justify-between flex-wrap gap-6">
        <div className="flex gap-16 flex-wrap">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.2em]">Active Caseload</span>
            <span className="text-3xl font-extrabold text-slate-900 tracking-tighter">{stats?.totalPatients || 0}</span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.2em]">Open Symptoms</span>
            <span className="text-3xl font-extrabold text-error tracking-tighter">{stats?.activeSymptoms || 0}</span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.2em]">Urgent / High</span>
            <span className="text-3xl font-extrabold text-amber-500 tracking-tighter">{stats?.urgentCases || 0}</span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.2em]">Resolved Today</span>
            <span className="text-3xl font-extrabold text-secondary tracking-tighter">{stats?.resolvedCases || 0}</span>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right hidden sm:block">
            <p className="text-xs font-bold text-slate-900">System Synchronized</p>
            <p className="text-xs text-slate-500 font-medium">
              {lastFetchedAt ? `Updated ${getRelativeTime(lastFetchedAt)}` : 'Syncing…'}
            </p>
          </div>
          <button
            onClick={() => fetchData()}
            disabled={isFetching}
            className="bg-slate-50 text-slate-500 p-3.5 rounded-2xl border border-outline-variant/20 hover:text-primary hover:bg-white hover:shadow-sm transition-all group disabled:opacity-50"
            title="Refresh dashboard"
          >
            <RefreshCw className={`w-5 h-5 transition-transform duration-500 ${isFetching ? 'animate-spin' : 'group-active:rotate-180'}`} />
          </button>
        </div>
      </section>
      {/* Delegate Action Modal */}
      {delegateModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50" onClick={() => setDelegateModal(null)}>
          <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <div>
                <h3 className="font-headline font-bold text-lg text-slate-900">Delegate Action</h3>
                <p className="text-xs text-slate-500 mt-0.5 capitalize">{delegateModal.action}</p>
              </div>
              <button onClick={() => setDelegateModal(null)} className="p-2 rounded-xl hover:bg-slate-100 transition-all">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Patient context */}
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Patient</p>
                <p className="font-bold text-slate-900">{delegateModal.patient.name}</p>
                <p className="text-xs text-slate-500 capitalize mt-0.5">{delegateModal.reason}</p>
              </div>

              {/* Doctor picker */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-widest mb-2">Delegate to</label>
                {loadingCareTeam ? (
                  <div className="h-11 rounded-xl bg-slate-100 animate-pulse" />
                ) : careTeamDoctors.length === 0 ? (
                  <p className="text-sm text-slate-500 py-3 text-center">No other doctors on this patient's care team</p>
                ) : (
                  <div className="relative">
                    <select
                      value={selectedDoctorId}
                      onChange={e => setSelectedDoctorId(e.target.value)}
                      className="w-full appearance-none border border-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 pr-10"
                    >
                      <option value="">Select a doctor…</option>
                      {careTeamDoctors.map(doc => (
                        <option key={doc._id} value={doc._id}>
                          Dr. {doc.name} — {doc.role}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                )}
              </div>

              {/* Optional note */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-widest mb-2">Note <span className="font-normal text-slate-400 lowercase normal-case">(optional)</span></label>
                <textarea
                  value={delegationNote}
                  onChange={e => setDelegationNote(e.target.value)}
                  placeholder="Add context for the receiving doctor…"
                  rows={2}
                  className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => setDelegateModal(null)}
                  className="flex-1 py-3 border border-slate-200 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  disabled={!selectedDoctorId || delegating || loadingCareTeam || careTeamDoctors.length === 0}
                  onClick={handleDelegate}
                  className="flex-1 py-3 bg-primary text-white rounded-xl text-sm font-bold hover:bg-primary/90 active:scale-95 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  {delegating ? 'Delegating…' : 'Delegate'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Follow-up scheduling modal */}
      {followUpModal && (
        <ClinicianActionModal
          actionKey="follow_up_scheduling"
          patientId={followUpModal.patientId}
          onClose={() => setFollowUpModal(null)}
          onScheduled={() => {
            toast.success('Follow-up scheduled — patient notified');
          }}
          onActionExecuted={async () => {
            await handleExecuteAction(followUpModal.assessmentId, followUpModal.actionType, followUpModal.patientId, followUpModal.cardIndex);
            setFollowUpModal(null);
          }}
        />
      )}
    </div>
  );
};

export default DoctorDashboard;
